import { Component } from '@theme/component';
import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

const CONTENT_SELECTOR = '[data-quick-view-content]';
const MODAL_MOUNT_ID = 'quick-view-modal-content';
const ADDED_LABEL_DURATION = 1600;
const ADDING_TO_ADDED_DELAY = 650;

/**
 * Trigger rendered on every product card (snippets/quick-view-trigger.liquid).
 * Delegates to the single, shared <quick-view-dialog> instance.
 */
export class QuickViewTriggerComponent extends Component {
  open() {
    const url = this.dataset.productUrl;
    if (!url) return;

    const dialog = document.getElementById('quick-view-dialog');
    if (dialog instanceof QuickViewDialogComponent) {
      dialog.openForProduct(url);
    }
  }
}

if (!customElements.get('quick-view-trigger')) {
  customElements.define('quick-view-trigger', QuickViewTriggerComponent);
}

/**
 * Global Quick View dialog (snippets/quick-view-dialog.liquid). Fetches the real
 * product markup from sections/quick-view-content.liquid via the Section Rendering
 * API and hydrates image switching, variant selection and add-to-cart feedback.
 */
export class QuickViewDialogComponent extends DialogComponent {
  /** @type {Map<string, string>} */
  #cache = new Map();
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {number} */
  #requestToken = 0;
  /** @type {(() => void) | null} */
  #cleanupHydration = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleContentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleContentClick);
    this.#abortController?.abort();
    this.#cleanupHydration?.();
  }

  /**
   * @param {string} productUrl
   */
  async openForProduct(productUrl) {
    const token = ++this.#requestToken;
    const mount = document.getElementById(MODAL_MOUNT_ID);
    if (!mount) return;

    this.#cleanupHydration?.();
    this.#cleanupHydration = null;

    const cached = this.#cache.get(productUrl);

    if (cached) {
      mount.innerHTML = cached;
      this.#cleanupHydration = this.#hydrate(mount);
    } else {
      mount.innerHTML = this.#skeletonMarkup(mount);
    }

    this.showDialog();

    if (cached) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const separator = productUrl.includes('?') ? '&' : '?';
      const fetchUrl = `${productUrl}${separator}section_id=quick-view-content&view=quick-view`;

      const response = await fetch(fetchUrl, { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Quick View fetch failed: ${response.status}`);

      const text = await response.text();

      // A newer request has already superseded this one — drop the stale response.
      if (token !== this.#requestToken) return;

      const doc = new DOMParser().parseFromString(text, 'text/html');
      const content = doc.querySelector(CONTENT_SELECTOR);

      if (!content) throw new Error('Quick View content not found in response');

      this.#cache.set(productUrl, content.outerHTML);

      mount.innerHTML = content.outerHTML;
      this.#cleanupHydration = this.#hydrate(mount);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (token !== this.#requestToken) return;

      mount.innerHTML = this.#errorMarkup(productUrl);
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {HTMLElement} mount
   * @returns {string}
   */
  #skeletonMarkup(mount) {
    const existingSkeleton = document.querySelector('template[data-qv-skeleton-template]');
    if (existingSkeleton instanceof HTMLTemplateElement) {
      return existingSkeleton.innerHTML;
    }
    return mount.innerHTML;
  }

  /**
   * @param {string} productUrl
   * @returns {string}
   */
  #errorMarkup(productUrl) {
    return `
      <div class="qv-error" role="alert">
        <p>Couldn't load this product.</p>
        <button type="button" class="qv-error__retry" data-qv-retry data-qv-retry-url="${productUrl}">Try again</button>
      </div>
    `;
  }

  /**
   * @param {Event} event
   */
  #handleContentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const retryButton = target.closest?.('[data-qv-retry]');
    if (retryButton instanceof HTMLElement) {
      const url = retryButton.dataset.qvRetryUrl;
      if (url) this.openForProduct(url);
    }
  };

  /**
   * Wires image switching, variant selection, and add-to-cart label feedback for
   * a freshly-injected content fragment. Returns a cleanup function.
   *
   * @param {HTMLElement} mount
   * @returns {() => void}
   */
  #hydrate(mount) {
    const root = mount.querySelector(CONTENT_SELECTOR);
    if (!root) return () => {};

    const cleanups = [
      this.#hydrateGallery(root),
      this.#hydrateVariants(root),
      this.#hydrateAddToCart(root),
      this.#hydrateSwipeToDismiss(),
    ];

    return () => cleanups.forEach((fn) => fn?.());
  }

  /**
   * @param {Element} root
   */
  #hydrateGallery(root) {
    const images = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-qv-image]')));
    const thumbs = /** @type {HTMLElement[]} */ (Array.from(root.querySelectorAll('[data-qv-thumb]')));

    if (images.length) images[0].toggleAttribute('data-qv-active', true);

    /** @param {number} index */
    const setActive = (index) => {
      images.forEach((img) => img.toggleAttribute('data-qv-active', Number(img.dataset.qvImage) === index));
      thumbs.forEach((thumb) => {
        const isActive = Number(thumb.dataset.qvThumb) === index;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-selected', String(isActive));
      });
    };

    /** @param {Event} event */
    const onClick = (event) => {
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const index = Number(target.dataset.qvThumb);
      if (!Number.isNaN(index)) setActive(index);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', onClick));

    return () => thumbs.forEach((thumb) => thumb.removeEventListener('click', onClick));
  }

  /**
   * Mirrors the option/variant matching used on the PDP (assets/pdp-script.js):
   * find the variant whose `options` array matches the selected values in order.
   *
   * @param {Element} root
   */
  #hydrateVariants(root) {
    const optionsRoot = root.querySelector('[data-qv-options]');
    const jsonScript = root.querySelector('[data-qv-product-json]');
    if (!optionsRoot || !jsonScript?.textContent) return () => {};

    /** @type {{variants: Array<{id: number, options: string[], available: boolean, price: number, compare_at_price: number|null}>}} */
    const productData = JSON.parse(jsonScript.textContent);

    const variantIdInput = /** @type {HTMLInputElement | null} */ (root.querySelector('input[name="id"]'));
    const groups = Array.from(optionsRoot.querySelectorAll('.qv-option-group'));

    const selected = groups.map((group) => {
      const selectedBtn = group.querySelector('.qv-option-value.is-selected');
      return selectedBtn instanceof HTMLElement ? selectedBtn.dataset.qvOptionValue ?? '' : '';
    });

    /** @param {string[]} options */
    const findVariant = (options) =>
      productData.variants.find((variant) => variant.options.every((opt, i) => opt === options[i]));

    const refreshAvailability = () => {
      groups.forEach((group, groupIndex) => {
        const buttons = /** @type {HTMLElement[]} */ (Array.from(group.querySelectorAll('.qv-option-value')));
        buttons.forEach((button) => {
          const candidate = selected.slice();
          candidate[groupIndex] = button.dataset.qvOptionValue ?? '';
          const variant = findVariant(candidate);
          button.toggleAttribute('data-unavailable', !variant || !variant.available);
        });
      });
    };

    const applyVariant = () => {
      const variant = findVariant(selected);
      if (!variant || !variantIdInput) return;

      variantIdInput.value = String(variant.id);
      variantIdInput.disabled = !variant.available;

      const moneyFormat = root.getAttribute('data-money-format') || '{{amount}}';
      const currency = root.getAttribute('data-currency') || 'USD';
      const formatted = formatMoney(variant.price, moneyFormat, currency);

      root.querySelectorAll('[data-qv-price]').forEach((priceEl) => {
        priceEl.textContent = formatted;
      });

      const addButton = root.querySelector('[data-qv-add-btn]');
      const label = root.querySelector('[data-qv-add-label]');
      if (addButton instanceof HTMLButtonElement && label) {
        if (variant.available) {
          addButton.disabled = false;
          addButton.classList.remove('qv-add-btn--oos');
          addButton.type = 'submit';
          addButton.removeAttribute('data-oos');
          label.textContent = addButton.dataset.labelIdle || 'Add to Cart';
        } else {
          addButton.disabled = false;
          addButton.classList.add('qv-add-btn--oos');
          addButton.type = 'button';
          addButton.setAttribute('data-oos', 'true');
          label.textContent = 'Notify Me';
        }
      }

      refreshAvailability();
    };

    /** @param {Event} event */
    const onOptionClick = (event) => {
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const group = target.closest('.qv-option-group');
      if (!group) return;

      const groupIndex = groups.indexOf(group);
      if (groupIndex === -1) return;

      group.querySelectorAll('.qv-option-value').forEach((btn) => btn.classList.remove('is-selected'));
      target.classList.add('is-selected');
      selected[groupIndex] = target.dataset.qvOptionValue ?? '';

      applyVariant();
    };

    const buttons = Array.from(optionsRoot.querySelectorAll('.qv-option-value'));
    buttons.forEach((button) => button.addEventListener('click', onOptionClick));

    refreshAvailability();

    return () => buttons.forEach((button) => button.removeEventListener('click', onOptionClick));
  }

  /**
   * Drives the design's label-phase feedback (Add to Cart → Adding… → Added ✓ → revert)
   * around the theme's real add-to-cart submission (product-form-component /
   * assets/product-form.js), so the sitewide cart drawer still opens/updates natively.
   *
   * @param {Element} root
   */
  #hydrateAddToCart(root) {
    const form = root.querySelector('product-form-component form');
    const variantIdInput = /** @type {HTMLInputElement | null} */ (root.querySelector('input[name="id"]'));
    const button = root.querySelector('[data-qv-add-btn]');
    const label = root.querySelector('[data-qv-add-label]');
    if (!(button instanceof HTMLButtonElement) || !label) return () => {};

    /** @type {number | undefined} */
    let resetTimeout;
    /** Set on submit so the async cart:update/cart:error response can be matched back to this click. */
    let pendingVariantId = '';
    let pendingFormId = '';

    const setLabel = (text) => {
      label.textContent = text;
    };

    const onOosClick = () => {
      if (button.dataset.oos !== 'true') return;
      clearTimeout(resetTimeout);
      setLabel(button.dataset.labelAdded || "We'll notify you");
      resetTimeout = window.setTimeout(() => setLabel(button.dataset.labelIdle || 'Notify Me'), ADDED_LABEL_DURATION);
    };

    const onSubmit = () => {
      if (button.dataset.oos === 'true') return;
      clearTimeout(resetTimeout);
      button.disabled = true;
      setLabel(button.dataset.labelAdding || 'Adding…');
      // AddToCartEvent's sourceId is the variant id; CartErrorEvent's is the form id
      // (assets/product-form.js) — capture both since we can't know which will fire.
      pendingVariantId = variantIdInput?.value ?? '';
      pendingFormId = form?.id ?? '';
    };

    /** @param {CustomEvent} event */
    const onCartUpdate = (event) => {
      if (button.dataset.oos === 'true') return;

      const isError = event.type === ThemeEvents.cartError || event.detail?.data?.didError;
      const sourceId = event.detail?.sourceId;
      const matches = isError ? sourceId === pendingFormId : sourceId === pendingVariantId;
      if (!matches) return;

      button.disabled = false;

      if (isError) {
        setLabel(button.dataset.labelIdle || 'Add to Cart');
        return;
      }

      resetTimeout = window.setTimeout(() => {
        setLabel(button.dataset.labelAdded || 'Added ✓');
        resetTimeout = window.setTimeout(
          () => setLabel(button.dataset.labelIdle || 'Add to Cart'),
          ADDED_LABEL_DURATION
        );
      }, ADDING_TO_ADDED_DELAY);
    };

    button.addEventListener('click', onOosClick);
    form?.addEventListener('submit', onSubmit);
    document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate);
    document.addEventListener(ThemeEvents.cartError, onCartUpdate);

    return () => {
      clearTimeout(resetTimeout);
      button.removeEventListener('click', onOosClick);
      form?.removeEventListener('submit', onSubmit);
      document.removeEventListener(ThemeEvents.cartUpdate, onCartUpdate);
      document.removeEventListener(ThemeEvents.cartError, onCartUpdate);
    };
  }

  /**
   * Swipe-down-to-dismiss for the mobile bottom sheet.
   */
  #hydrateSwipeToDismiss() {
    const dialog = this.refs.dialog;
    const handle = this.querySelector('.qv-dialog__handle');
    if (!dialog || !handle) return () => {};

    let startY = 0;
    let currentY = 0;
    let dragging = false;

    /** @param {TouchEvent} event */
    const onTouchStart = (event) => {
      if (window.innerWidth >= 750) return;
      dragging = true;
      startY = event.touches[0].clientY;
      dialog.style.transition = 'none';
    };

    /** @param {TouchEvent} event */
    const onTouchMove = (event) => {
      if (!dragging) return;
      currentY = Math.max(0, event.touches[0].clientY - startY);
      dialog.style.transform = `translateY(${currentY}px)`;
    };

    const onTouchEnd = () => {
      if (!dragging) return;
      dragging = false;
      dialog.style.transition = '';
      dialog.style.transform = '';

      if (currentY > 80) this.closeDialog();
      currentY = 0;
    };

    handle.addEventListener('touchstart', onTouchStart, { passive: true });
    handle.addEventListener('touchmove', onTouchMove, { passive: true });
    handle.addEventListener('touchend', onTouchEnd);

    return () => {
      handle.removeEventListener('touchstart', onTouchStart);
      handle.removeEventListener('touchmove', onTouchMove);
      handle.removeEventListener('touchend', onTouchEnd);
    };
  }
}

if (!customElements.get('quick-view-dialog')) {
  customElements.define('quick-view-dialog', QuickViewDialogComponent);
}
